/**
 * Shared link-validity presentation for Workspace shared asset libraries
 * (ADR-019) — one place for the plain-language invalid-reason copy and the
 * validity badges used by BOTH sides of a link:
 *
 *   - `LinkValidityBadge` — the arche-side view (`ArcheWorkspaceLink`),
 *     rendered in `WorkspaceLinksSection`.
 *   - `LinkedArcheValidity` — the workspace-side view
 *     (`WorkspaceLinkedArche`), rendered on the Workspace Assets page.
 *
 * The server derives validity on every read; these components only render
 * what it said.
 */

import { Badge } from '@/components/ui/badge';
import type {
  ArcheWorkspaceLink,
  WorkspaceLinkInvalidReason,
  WorkspaceLinkedArche,
} from '@/lib/api';

/** Plain-language reason a link no longer qualifies — shown next to the badge. */
export function describeLinkInvalidReason(reason: WorkspaceLinkInvalidReason | null): string {
  switch (reason) {
    case 'revoked':
      return 'the link was revoked';
    case 'holder_archived':
      return 'the workspace was deleted';
    case 'arche_deleted':
      return 'the linked arche was deleted';
    case 'library_disabled':
      return 'the workspace library is disabled';
    case 'library_suspended':
      return 'the workspace library is suspended';
    case 'no_qualifying_member':
      return 'no workspace member can publish this arche any more';
    default:
      return 'the link no longer qualifies';
  }
}

/**
 * State of a revoked link, either side. `pinned` is the part that matters
 * after a soft revoke: the live published version keeps serving the shared
 * assets (and the workspace keeps paying) until the arche republishes or
 * the workspace hard-revokes.
 */
export function RevokedLinkState({ pinned, reason }: { pinned: boolean; reason?: string | null }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Badge variant="outline">Revoked</Badge>
      {pinned ? (
        <>
          <Badge variant="secondary">Still pinned</Badge>
          <span className="text-xs text-muted-foreground">
            the live published version keeps serving these assets until the arche republishes
          </span>
        </>
      ) : (
        <span className="text-xs text-muted-foreground">no longer served</span>
      )}
      {reason && <span className="text-xs text-muted-foreground">{reason}</span>}
    </span>
  );
}

/** Arche-side validity badge for one linked workspace. */
export function LinkValidityBadge({ link }: { link: ArcheWorkspaceLink }) {
  if (link.revokedAt) {
    return <RevokedLinkState pinned={link.pinned} />;
  }
  if (!link.validity.valid) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2">
        <Badge variant="destructive">Will be dropped on next publish</Badge>
        <span className="text-xs text-muted-foreground">
          {describeLinkInvalidReason(link.validity.reason)}
        </span>
      </span>
    );
  }
  if (!link.latestManifestId) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2">
        <Badge variant="secondary">Library not published yet</Badge>
        <span className="text-xs text-muted-foreground">
          nothing to pin until the workspace publishes its library
        </span>
      </span>
    );
  }
  return <Badge variant="secondary">Valid</Badge>;
}

/** Workspace-side validity badge for one linked arche. */
export function LinkedArcheValidity({ link }: { link: WorkspaceLinkedArche }) {
  if (link.revokedAt) {
    return <RevokedLinkState pinned={link.pinned} reason={link.revokeReason} />;
  }
  if (!link.validity.valid) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2">
        <Badge variant="destructive">Will be dropped on next publish</Badge>
        <span className="text-xs text-muted-foreground">
          {describeLinkInvalidReason(link.validity.reason)}
        </span>
      </span>
    );
  }
  return <Badge variant="secondary">Valid</Badge>;
}
